import type { PharmaVariantMetadata } from '@lib/types/pharma';

type VariantForConsultation = {
  metadata?: Record<string, unknown> | null;
};

export function requiresConsultation(
  variant: VariantForConsultation | undefined | null,
): boolean {
  if (!variant) {
    return false;
  }
  const meta = variant.metadata as PharmaVariantMetadata | undefined;
  return Boolean(meta?.questionnaire_id);
}

export function getQuestionnaireSlug(
  variant: VariantForConsultation | undefined | null,
): string | null {
  if (!variant) {
    return null;
  }
  const meta = variant.metadata as PharmaVariantMetadata | undefined;
  return meta?.questionnaire_id ?? null;
}

export type ConsultationMode = 'modal' | 'full_page';

/**
 * Returns the consultation flow a variant should trigger, derived from its
 * legal classification:
 *   - 'modal'     — P med inline modal
 *   - 'full_page' — POM full-page wizard
 *   - null        — no consultation required (GSL or missing questionnaire)
 */
export function getConsultationMode(
  variant: VariantForConsultation | undefined | null,
): ConsultationMode | null {
  if (!variant) {
    return null;
  }
  const meta = variant.metadata as PharmaVariantMetadata | undefined;
  if (!meta?.questionnaire_id) {
    return null;
  }
  return classificationToMode(meta.legal_classification);
}

function classificationToMode(
  classification: PharmaVariantMetadata['legal_classification'],
): ConsultationMode | null {
  if (classification === 'P') {
    return 'modal';
  }
  if (classification === 'POM') {
    return 'full_page';
  }
  return null;
}

/**
 * The buy-flow state the PDP CTA should render. Centralising this here keeps
 * the rules covered by Layer 1 unit tests rather than scattering them across
 * the desktop and mobile CTA components.
 *
 *   - 'mounting'      — pre-mount/loading. Disabled placeholder so the
 *                       button never flashes the wrong text on hydrate.
 *   - 'no-variant'    — multi-variant product with no option picked yet.
 *   - 'unclassified'  — variant has no legal_classification (pharmacy
 *                       safety: default to deny). Also covers a P/POM
 *                       variant that's missing its questionnaire_id.
 *   - 'not-for-sale'  — variant published for SEO but not merch-reviewed
 *                       (not_for_sale flag). Page stays live; never buyable.
 *   - 'gsl'           — direct add-to-basket allowed (GSL classification).
 *   - 'other'         — direct add-to-basket allowed (Other/unregulated
 *                       classification, e.g. cosmetics or supplements).
 *   - 'consultation'  — P or POM, requires consultation completion before
 *                       the line can be added. Mode tells us which UI.
 */
export type CTAState =
  | { kind: 'mounting' }
  | { kind: 'no-variant' }
  | { kind: 'unclassified' }
  | { kind: 'not-for-sale' }
  | { kind: 'gsl' }
  | { kind: 'other' }
  | { kind: 'consultation'; mode: ConsultationMode };

export function getCTAState(
  mounted: boolean,
  variant: VariantForConsultation | undefined | null,
): CTAState {
  if (!mounted) {
    return { kind: 'mounting' };
  }
  if (!variant) {
    return { kind: 'no-variant' };
  }
  const meta = variant.metadata as PharmaVariantMetadata | undefined;
  // Carried-over-but-unreviewed products are never purchasable, regardless of
  // classification or stock. Checked before everything else so an unreviewed
  // product that DOES carry a dm+d classification still can't be bought.
  if (meta?.not_for_sale === true) {
    return { kind: 'not-for-sale' };
  }
  const classification = meta?.legal_classification;
  if (!classification) {
    return { kind: 'unclassified' };
  }
  if (classification === 'GSL') {
    return { kind: 'gsl' };
  }
  if (classification === 'OTHER') {
    return { kind: 'other' };
  }
  // P or POM — must route through a consultation. A questionnaire_id is
  // required to actually run the flow; without it we can't safely proceed.
  if (!meta?.questionnaire_id) {
    return { kind: 'unclassified' };
  }
  const mode = classificationToMode(classification);
  if (!mode) {
    return { kind: 'unclassified' };
  }
  return { kind: 'consultation', mode };
}
